import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatINR } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Download, FileText, Share2, MessageCircle, Pencil } from 'lucide-react';

const STATUS_COLORS = {
  Draft: 'bg-slate-100 text-slate-700',
  Sent: 'bg-blue-100 text-blue-700',
  Approved: 'bg-green-100 text-green-700',
  Order: 'bg-amber-100 text-amber-700',
  Delivered: 'bg-purple-100 text-purple-700',
  Installed: 'bg-emerald-100 text-emerald-700',
};
const STATUSES = ['Draft', 'Sent', 'Approved', 'Order', 'Delivered', 'Installed'];

export default function QuotationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    api.get(`/quotations/${id}`).then(r => setQuotation(r.data)).catch(() => navigate('/quotations'));
  }, [id, navigate]);

  const updateStatus = async (status) => {
    try {
      const { data } = await api.patch(`/quotations/${id}/status`, { status });
      setQuotation(data);
    } catch {}
  };

  const downloadPdf = async () => {
    try {
      const { data } = await api.get(`/quotations/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quotation?.quote_number || 'quotation'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  // Sanitize phone number → WhatsApp international format (default country: India 91)
  const formatWaNumber = (phone) => {
    if (!phone) return '';
    let p = String(phone).replace(/[^0-9]/g, '');
    if (!p) return '';
    // If starts with 0 (local format) → strip it and assume India
    if (p.startsWith('0')) p = p.replace(/^0+/, '');
    // 10 digits → assume Indian number, prepend 91
    if (p.length === 10) p = '91' + p;
    // Already has country code (11-13 digits) → keep as-is
    return p;
  };

  const shareWhatsApp = async () => {
    setSharing(true);
    try {
      // Download the PDF first
      const { data: pdfBlob } = await api.get(`/quotations/${id}/pdf`, { responseType: 'blob' });
      const filename = `${quotation?.quote_number || 'quotation'}.pdf`;
      const file = new File([pdfBlob], filename, { type: 'application/pdf' });

      const grand = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(q.grand_total);
      const items = q?.items || [];
      const itemsSummary = items.slice(0, 3).map(i => `${i.design_type} (${i.width_mm}x${i.height_mm}mm)`).join(', ');
      const moreItems = items.length > 3 ? ` +${items.length - 3} more` : '';
      const msgText = [
        `*Quotation from Leela Solutions*`,
        `Quote: ${q.quote_number}`,
        `Customer: ${q.customer_name}`,
        `Items: ${itemsSummary}${moreItems}`,
        `*Grand Total: ${grand}*`,
      ].join('\n');

      const waNumber = formatWaNumber(q.customer_phone);

      // Prefer native Web Share API with files (mobile: WhatsApp appears in share sheet & sends PDF directly)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: `Quotation ${q.quote_number}`,
            text: msgText,
          });
          setSharing(false);
          return;
        } catch (err) {
          // User cancelled share — don't fall through
          if (err && err.name === 'AbortError') { setSharing(false); return; }
        }
      }

      // Desktop fallback: auto-download the PDF, then open WhatsApp Web with the message.
      // User attaches the already-downloaded file themselves (WhatsApp doesn't allow file upload via URL).
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      const fallbackMsg = msgText + `\n\n_PDF "${filename}" downloaded — please attach it to this chat._`;
      // If customer has a phone number, open chat directly with that contact
      const waUrl = waNumber
        ? `https://wa.me/${waNumber}?text=${encodeURIComponent(fallbackMsg)}`
        : `https://wa.me/?text=${encodeURIComponent(fallbackMsg)}`;
      window.open(waUrl, '_blank');
    } catch (e) {
      alert('Failed to share quotation PDF');
    }
    setSharing(false);
  };

  if (!quotation) return <div className="flex items-center justify-center h-64 text-slate-400">Loading...</div>;

  const q = quotation;

  return (
    <div className="space-y-6" data-testid="quotation-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/quotations')} data-testid="back-btn"><ArrowLeft size={16} /></Button>
          <div>
            <h1 className="font-['Outfit'] text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
              <FileText size={22} className="text-[#1a365d]" />
              {q.quote_number}
            </h1>
            <p className="text-slate-500 text-sm">{q.date?.slice(0, 10)} | Valid until {q.valid_until?.slice(0, 10)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={q.status} onValueChange={updateStatus}>
            <SelectTrigger className="w-32" data-testid="status-select">
              <Badge variant="secondary" className={STATUS_COLORS[q.status]}>{q.status}</Badge>
            </SelectTrigger>
            <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => navigate(`/quotations/${id}/edit`)} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="edit-quotation-btn">
            <Pencil size={16} className="mr-1.5" /> Edit
          </Button>
          <Button onClick={shareWhatsApp} variant="outline" className="border-green-500 text-green-600 hover:bg-green-50" disabled={sharing} data-testid="whatsapp-share-btn" title={q.customer_phone ? `Opens WhatsApp chat with ${q.customer_name} (+${formatWaNumber(q.customer_phone)})` : 'Opens WhatsApp contact picker'}>
            <MessageCircle size={16} className="mr-1.5" /> {sharing ? 'Preparing PDF...' : (q.customer_phone ? `Send PDF to ${q.customer_name?.split(' ')[0] || 'Customer'}` : 'Send PDF on WhatsApp')}
          </Button>
          <Button onClick={downloadPdf} className="bg-[#ed8936] hover:bg-[#dd6b20] text-white" data-testid="download-pdf-btn">
            <Download size={16} className="mr-1.5" /> Download PDF
          </Button>
        </div>
      </div>

      {/* Customer Info */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-['Outfit'] text-slate-500 uppercase tracking-wider">Customer</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div><span className="text-slate-400 text-xs block">Name</span><span className="font-medium">{q.customer_name || '-'}</span></div>
            <div><span className="text-slate-400 text-xs block">Phone</span><span>{q.customer_phone || '-'}</span></div>
            <div><span className="text-slate-400 text-xs block">Email</span><span>{q.customer_email || '-'}</span></div>
            <div><span className="text-slate-400 text-xs block">Address</span><span>{q.customer_address || '-'}</span></div>
          </div>
          {q.delivery_address && (
            <div className="mt-2 text-sm"><span className="text-slate-400 text-xs block">Delivery Address</span><span>{q.delivery_address}</span></div>
          )}
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-['Outfit'] text-slate-500 uppercase tracking-wider">Line Items</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="detail-items-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 font-medium text-slate-600">S.No</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Design</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Details</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">W(mm)</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">H(mm)</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Qty</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Area(Sqm)</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Glass</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Unit Price</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Total</th>
                </tr>
              </thead>
              <tbody>
                {q.items?.map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3">{item.sno}</td>
                    <td className="px-4 py-3 font-medium">{item.design_type}</td>
                    <td className="px-4 py-3 text-[10px] text-slate-500 leading-tight max-w-[180px]">
                      <div className="space-y-0.5">
                        {item.series && <div>Series: {item.series}</div>}
                        {item.color && <div>Color: {item.color}</div>}
                        {item.net_type && <div>Net: {item.net_type}</div>}
                      {item.hardware_type && <div>Handle: {item.hardware_type}</div>}
                        {item.handle_type && <div>Handle: {item.handle_type}</div>}
                        {item.hinge_type && <div>Hinge: {item.hinge_type}</div>}
                        {item.frame && <div>Frame: {item.frame}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{item.width_mm}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{item.height_mm}</td>
                    <td className="px-4 py-3 text-right">{item.qty}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{item.area_sqm?.toFixed(3)}</td>
                    <td className="px-4 py-3 text-xs">{item.glass_type}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{formatINR(item.unit_price)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-medium">{formatINR(item.total_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Totals */}
      <Card className="border-2 border-[#1a365d]">
        <CardContent className="p-5">
          <div className="max-w-sm ml-auto space-y-2">
            <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-mono">{formatINR(q.subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Transport</span><span className="font-mono">{formatINR(q.transport_charges)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Installation</span><span className="font-mono">{formatINR(q.installation_charges || 0)}</span></div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{q.gst_type === 'CGST_SGST' ? 'CGST + SGST' : 'IGST'} ({q.gst_rate}%)</span>
              <span className="font-mono">{formatINR(q.gst_amount)}</span>
            </div>
            {q.gst_type === 'CGST_SGST' && (
              <div className="text-xs text-slate-400 text-right">
                CGST: {formatINR(Math.round(q.gst_amount / 2))} | SGST: {formatINR(Math.round(q.gst_amount / 2))}
              </div>
            )}
            <div className="border-t-2 border-[#1a365d] pt-3 flex justify-between items-center">
              <span className="font-['Outfit'] font-semibold text-lg text-[#1a365d]">Grand Total</span>
              <span className="font-['Outfit'] font-bold text-3xl text-[#1a365d]" data-testid="detail-grand-total">{formatINR(q.grand_total)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Terms */}
      {q.terms && (
        <Card className="border border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-['Outfit'] text-slate-500 uppercase tracking-wider">Terms & Conditions</CardTitle></CardHeader>
          <CardContent>
            <pre className="text-sm text-slate-600 whitespace-pre-wrap font-sans">{q.terms}</pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
